import { FC, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DropdownField, Heading, Text, useControlledField } from '@frontend/design-system';
import { DEFAULT_POSTAL_CODE_LOCALE } from '../../../constants';
import { getStateLabel, getZipCodeLabel, LOCALE_LIST } from '../../../helpers/locale-helpers';
import { useGetStateList } from '../../../hooks/api-hooks';
import { useLeavePageConfirm } from '../../../hooks/use-leave-page-confirm';
import { useConfigShallowStore } from '../../../stores';
import { KioskTypes } from '../../../types';
import { DynamicFormContainer } from './dynamic-form-container/dynamic-form-container';
import { SchemaForm } from './dynamic-form-container/types';

const containerStyles = css`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing(2)};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.floating};
`;

const headingContainerStyles = css`
  margin-bottom: ${theme.spacing(2)};
`;

const languageSelectionStyles = css`
  margin-bottom: ${theme.spacing(1.5)};
`;

const languageSelectionLabelStyles = css`
  margin-bottom: ${theme.spacing(0.5)};
`;

// Values submitted by PatronForm; all field types rendered here resolve to string values
export interface PatronFormValues {
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  city?: string;
  address1?: string;
  address2?: string;
  state?: string;
  postalCode?: string;
  email?: string;
  mobilePhone?: string;
}

interface Props {
  patronType: KioskTypes.Patron;
  requireExistingPatronOptionalFields?: boolean;
  onSubmit: (values: PatronFormValues) => void;
}

// Fields not present in pre-registration form
const EXTENDED_FIELDS = new Set([
  'gender',
  'homePhone',
  'preferredName',
  'workPhone',
  'middleName',
  'driversLicenseNumber',
  'maritalStatus',
]);
// Fields that should be optional for existing patron initial search
const EXISTING_PATRON_OPTIONAL_FIELDS = new Set(['city', 'address1', 'state', 'postalCode', 'email']);

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

const hasAnyValue = (values: Record<string, unknown>) => {
  return Object.values(values).some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  });
};

export const PatronForm: FC<Props> = ({ onSubmit, patronType, requireExistingPatronOptionalFields }) => {
  const { t } = useTranslation('digital-forms-experience');
  const { postalCodeLocale } = useConfigShallowStore('postalCodeLocale');
  const { stateList } = useGetStateList(postalCodeLocale || DEFAULT_POSTAL_CODE_LOCALE);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const shouldHideExistingOptionalFields = patronType === 'existing' && !requireExistingPatronOptionalFields;

  const [selectedLanguage, setLanguage] = useState(i18next.language);
  const languageFieldProps = useControlledField({
    required: false,
    type: 'dropdown',
    value: selectedLanguage,
    onChange: (value) => {
      i18next.changeLanguage(value);
      setLanguage(value);
    },
  });

  const schema = useMemo<SchemaForm[]>(() => {
    const stateOptions = stateList.map(({ label, value = '' }) => ({ label: label ?? value, value }));
    const getExistingPatronFieldState = (fieldKey: string) => {
      const isOptionalFieldForExisting =
        shouldHideExistingOptionalFields && EXISTING_PATRON_OPTIONAL_FIELDS.has(fieldKey);

      return {
        required: !isOptionalFieldForExisting,
        hidden: isOptionalFieldForExisting,
      };
    };
    const address1FieldState = getExistingPatronFieldState('address1');

    const allFields = {
      firstName: {
        id: 'firstName',
        label: t('First Name'),
        required: true,
        hidden: false,
        meta: { type: 'text', dataGroup: 'person', dataKey: 'firstName' },
      },
      middleName: {
        id: 'middleName',
        label: t('Middle Name'),
        required: true,
        hidden: false,
        meta: { type: 'text', dataGroup: 'person', dataKey: 'middleName' },
      },
      lastName: {
        id: 'lastName',
        label: t('Last Name'),
        required: true,
        hidden: false,
        meta: { type: 'text', dataGroup: 'person', dataKey: 'lastName' },
      },
      birthdate: {
        id: 'birthdate',
        label: t('Date of Birth'),
        required: true,
        hidden: false,
        meta: { type: 'date', dataGroup: 'person', dataKey: 'birthdate' },
      },
      city: {
        id: 'city',
        label: t('City'),
        ...getExistingPatronFieldState('city'),
        meta: { type: 'text', dataGroup: 'address', dataKey: 'city' },
      },
      address1: {
        id: 'address1',
        label: t('Street Address 1'),
        ...address1FieldState,
        meta: { type: 'text', dataGroup: 'address', dataKey: 'address1' },
      },
      address2: {
        id: 'address2',
        label: t('Street Address 2'),
        required: false,
        hidden: address1FieldState.hidden,
        meta: { type: 'text', dataGroup: 'address', dataKey: 'address2' },
      },
      state: {
        id: 'state',
        label: getStateLabel(postalCodeLocale),
        ...getExistingPatronFieldState('state'),
        meta: { type: 'select', dataGroup: 'address', dataKey: 'state' },
        options: stateOptions,
        resetOnSchemaChange: true,
      },
      maritalStatus: {
        id: 'maritalStatus',
        label: t('Marital Status'),
        required: true,
        hidden: false,
        meta: { type: 'radio', dataGroup: 'person', dataKey: 'maritalStatus' },
        options: MARITAL_STATUS_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) })),
      },
      postalCode: {
        id: 'postalCode',
        label: getZipCodeLabel(postalCodeLocale),
        ...getExistingPatronFieldState('postalCode'),
        meta: { type: 'postalCode', dataGroup: 'address', dataKey: 'postalCode' },
        resetOnSchemaChange: true,
      },
      email: {
        id: 'email',
        label: t('Email Address'),
        ...getExistingPatronFieldState('email'),
        meta: { type: 'email', dataGroup: 'person', dataKey: 'email' },
      },
      mobilePhone: {
        id: 'mobilePhone',
        label: t('Mobile Phone'),
        required: true,
        hidden: false,
        meta: { type: 'phone', dataGroup: 'person', dataKey: 'mobilePhone' },
      },
      gender: {
        id: 'gender',
        label: t('Gender'),
        required: true,
        hidden: false,
        meta: { type: 'radio', dataGroup: 'person', dataKey: 'gender' },
        options: GENDER_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) })),
      },
      homePhone: {
        id: 'homePhone',
        label: t('Home Phone'),
        required: true,
        hidden: false,
        meta: { type: 'phone', dataGroup: 'person', dataKey: 'homePhone' },
      },
      preferredName: {
        id: 'preferredName',
        label: t('Preferred Name'),
        required: true,
        hidden: false,
        meta: { type: 'text', dataGroup: 'person', dataKey: 'preferredName' },
      },
      driversLicenseNumber: {
        id: 'driversLicenseNumber',
        label: t("Driver's License Number"),
        required: true,
        hidden: false,
        meta: { type: 'text', dataGroup: 'person', dataKey: 'driversLicenseNumber' },
      },
      workPhone: {
        id: 'workPhone',
        label: t('Work Phone'),
        required: true,
        hidden: false,
        meta: { type: 'phone', dataGroup: 'person', dataKey: 'workPhone' },
      },
    };

    const fields = Object.entries(allFields).reduce<Record<string, (typeof allFields)[keyof typeof allFields]>>(
      (acc, [fieldKey, fieldConfig]) => {
        if (EXTENDED_FIELDS.has(fieldKey)) {
          return acc;
        }

        acc[fieldKey] = fieldConfig;
        return acc;
      },
      {}
    );

    return [
      {
        form: {
          id: `kiosk-patron-${patronType}`,
          name: t('Patient Information'),
          sections: ['patron-section'],
        },
        sections: {
          'patron-section': {
            id: 'patron-section',
            title: t('Complete the following fields'),
            fields: Object.keys(fields),
          },
        },
        fields,
        conditions: [],
      },
    ];
  }, [patronType, postalCodeLocale, shouldHideExistingOptionalFields, stateList, t]);

  const { confirmationModal } = useLeavePageConfirm({
    shouldBlockFn: () => hasUnsavedChanges && patronType !== 'existing',
    title: t('You have unsaved changes'),
    message: t('Are you sure you want to leave? Your changes will be lost.'),
    deps: [hasUnsavedChanges, patronType],
  });

  const title =
    patronType === 'existing'
      ? t('Verify Identity')
      : patronType === 'pre-reg'
      ? t('Pre-Admission Registration Form')
      : t('New Patient');
  const subtitle =
    patronType === 'existing'
      ? t('Complete the following fields (required by HIPAA) to securely verify your identity')
      : t('Complete the following fields (required by HIPAA)');

  return (
    <>
      <section css={containerStyles}>
        <div css={headingContainerStyles}>
          <Heading as='h1' level={3}>
            {title}
          </Heading>
          <Text>{subtitle}</Text>
        </div>
        <div css={languageSelectionStyles}>
          <label htmlFor='language'>
            <Text css={languageSelectionLabelStyles}>{t('Select language')}</Text>
          </label>
          <DropdownField label='' {...languageFieldProps} id='language' name='language'>
            {LOCALE_LIST.map(({ name, value }) => (
              <DropdownField.Option key={value} value={value} searchValue={value}>
                {name}
              </DropdownField.Option>
            ))}
          </DropdownField>
        </div>
        <DynamicFormContainer
          schemaForms={schema}
          showSectionMeta={false}
          showPreviousButton={false}
          disableReviewMode
          renderAsCard={false}
          nextButtonTitle={t('Continue')}
          onValuesChange={(values) => {
            setHasUnsavedChanges(hasAnyValue(values));
          }}
          onSubmit={(values) => {
            setHasUnsavedChanges(false);
            setTimeout(() => {
              onSubmit(values as PatronFormValues);
            }, 0);
          }}
        />
      </section>
      {confirmationModal}
    </>
  );
};
